import React, { useEffect, useState } from "react";
import ButtonPrimary from "../Buttons/ButtonPrimary";
import BaseCard from "../Cards/BaseCard";
import Input from "../Forms/Input";
import BrijLogo from "../Assets/BrijLogoWithText.png";
import { inject, observer } from "mobx-react";
import { useGlobalStore } from "../../api/globalStores";
import toast from "react-hot-toast";
import { useNavigate, Navigate } from "react-router-dom";
import { supabase } from "../../api/supabase/client";
import { toJS } from "mobx";

export interface ILoginPage {
  type: "PARENT" | "TEACHER";
}

const placeholderName = {
  PARENT: "Parent",
  TEACHER: "Teacher",
};

export default observer(LoginPage);

function LoginPage(props: ILoginPage) {
  const [mode, setMode] = useState<"LOGIN" | "SIGN_UP">("LOGIN");
  const globalStores = useGlobalStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [referralCode, setReferralCode] = useState<string>();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      props.type === "PARENT" &&
      globalStores.parentStore.parentAccount?.id !== undefined
    ) {
      navigateToLoggedInPage();
    } else if (
      props.type === "TEACHER" &&
      globalStores.teacherStore.teacherAccount?.id !== undefined &&
      globalStores.teacherStore.staffDetail.isApproved
    ) {
      navigateToLoggedInPage();
    }
  }, [
    globalStores.teacherStore.teacherAccount?.id,
    globalStores.teacherStore.staffDetail.isApproved,
    globalStores.parentStore.parentAccount?.id,
  ]);

  const handleForgotPassword = async () => {
    if (email === "")
      throw new Error(
        "Please fill in your email in the form to reset your password! Click the button again once you have specified your email"
      );

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: process.env.REACT_APP_FRONTEND_URL!,
    });

    if (error) throw error;
  };

  const toggleMode = () => {
    if (mode === "LOGIN") {
      setMode("SIGN_UP");
    } else {
      setMode("LOGIN");
    }
  };

  const navigateToLoggedInPage = async () => {
    if (
      props.type === "TEACHER" &&
      globalStores.teacherStore.teacherAccount?.id !== undefined
    ) {
      navigate("/teacher/calendar");
      toast.success("Teacher Login Success!");
      return;
    } else if (
      props.type === "PARENT" &&
      globalStores.parentStore.parentAccount?.id !== undefined
    ) {
      if ((await globalStores.parentStore.getChildren()).length > 0) {
        navigate("/parent/child_selection");
        toast.success("Parent Login Success!");
      } else if (
        (await supabase.auth.getUser()).data.user?.email_confirmed_at
      ) {
        navigate("/parent/flow/parent_details_2");
        toast.success("Parent Login Success!");
      }
    } else {
      toast.error("Unable to Login!");
    }
  };

  const signIn = async () => {
    if (props.type === "TEACHER") {
      try {
        await globalStores.teacherStore.emailSignIn(email, password);
      } catch (e: any) {
        toast.error(e.message);
        return;
      }
    } else if (props.type === "PARENT") {
      try {
        await globalStores.parentStore.emailSignIn(email, password);
      } catch (e: any) {
        toast.error(e.message);
        return;
      }
    }
    //this was removed else the navigate thingy will run twice because there's already a useEffect hook looking for logged in status
    // try {
    //   await navigateToLoggedInPage();
    // } catch (e: any) {
    //   toast.error(e.message);
    //   return;
    // }
  };

  //TODO: Make loading indicator on clicking sign up
  //TODO: Test edge case where users sign up more than once
  //TODO: Handle asking for email confirmation
  //TODO: Make email confirmed page
  //TODO: Don't allow multiple signups
  const _signUp = async () => {
    if (!firstName || firstName === "") {
      throw new Error("Please enter a valid first name!");
    }
    if (!lastName || lastName === "") {
      throw new Error("Please enter a valid last name!");
    }
    if (props.type === "TEACHER") {
      await globalStores.teacherStore.emailSignUp(
        email,
        password,
        firstName,
        lastName
      );
      toast("Please accept the confirmation email in your inbox");
      setMode("LOGIN");
    } else if (props.type === "PARENT") {
      await globalStores.parentStore.emailSignUp(
        email,
        password,
        firstName,
        lastName,
        referralCode
      );
      toast("Please accept the confirmation email in your inbox");
      setMode("LOGIN");
    }
  };

  const signUp = () => {
    toast.promise(_signUp(), {
      success: "Sign Up Success!",
      error: (e) => {
        if (e.message === "Unable to validate email address: invalid format") {
          return "Please enter a valid email address!";
        }
        return e.message;
      },
      loading: "Signing Up...",
    });
  };

  return (
    <>
      {/* DESKTOP VIEW */}

      <div
        className="hidden
      bg-brij-Default h-full lg:flex relative bg-playground bg-contain bg-no-repeat bg-bottom backdrop-opacity-50 w-full"
      >
        <div className="p-14 flex w-full justify-between z-10">
          {/* TEXT */}
          <div className="w-[600px] inline-flex flex-col gap-2">
            <div className="font-semibold text-[4rem] leading-[123%] text-brij-Black">
              Master Speaking Chinese the Social Way
            </div>
            <div className="text-[2rem] font-medium leading-[140%] text-brij-Black">
              The Chinese Learning <br /> Community for Kids
            </div>
            {props.type === "TEACHER" && (
              <div className="text-[1.7rem] font-light leading-[140%] text-brij-Black pt-2">
                Welcome back, teachers!
              </div>
            )}
          </div>
          <div className="flex-col flex">
            <BaseCard>
              <img src={BrijLogo} alt="logo" className="w-[13.3rem]" />
              <div className="inline-flex flex-col gap-3 w-full">
                {mode === "SIGN_UP" && props.type === "PARENT" && (
                  <p className="text-xl pb-4 pl-3 text-center">
                    <b>Please input parent details</b>
                  </p>
                )}
                <Input
                  onChange={(newVal) => setEmail(newVal)}
                  type="email"
                  icon="mail"
                  placeholder={`${placeholderName[props.type]} Email Address`}
                />
                <Input
                  onChange={(newVal) => setPassword(newVal)}
                  type="password"
                  placeholder={`${placeholderName[props.type]} Password`}
                />
                {mode === "SIGN_UP" && (
                  <>
                    <Input
                      onChange={(newVal) => setFirstName(newVal)}
                      type="text"
                      placeholder={`${placeholderName[props.type]} First Name`}
                    />
                    <Input
                      onChange={(newVal) => setLastName(newVal)}
                      type="text"
                      placeholder={`${placeholderName[props.type]} Last Name`}
                    />
                    {props.type === "PARENT" && (
                      <Input
                        default={referralCode}
                        onChange={(referralCode) =>
                          setReferralCode(referralCode)
                        }
                        type="text"
                        placeholder="Referral Code"
                      />
                    )}
                  </>
                )}

                <ButtonPrimary
                  onClick={() => {
                    if (mode === "LOGIN") {
                      signIn();
                    } else {
                      signUp();
                    }
                  }}
                >
                  {mode === "LOGIN" ? "Sign In" : "Sign Up"}
                </ButtonPrimary>
              </div>
              <div className="inline-flex flex-col gap-3">
                <div
                  className="text-small text-center w-full cursor-pointer"
                  onClick={() => {
                    toast.promise(handleForgotPassword(), {
                      success: "Reset password email sent!",
                      error: (e) => e.message,
                      loading: "Sending password reset email...",
                    });
                  }}
                >
                  Forgot Password
                </div>
                <div className="text-small text-center w-full">
                  <>
                    {mode === "LOGIN"
                      ? "Don't have an account? "
                      : "Already have an account? "}
                    <button
                      className="underline font-semibold cursor-pointer"
                      onClick={toggleMode}
                    >
                      {mode === "LOGIN" ? "Sign Up" : "Sign In"}
                    </button>
                  </>
                </div>
              </div>
            </BaseCard>
          </div>
        </div>
      </div>

      {/* MOBILE VIEW */}

      <div className="lg:hidden flex flex-col gap-2 items-center bg-brij-Default h-full overflow-scroll relative bg-playground bg-contain bg-no-repeat bg-bottom backdrop-opacity-50 w-full p-8">
        <img src={BrijLogo} alt="logo" className="w-[10rem]" />

        <br />
        <div className="inline-flex flex-col gap-3 w-full">
          {mode === "SIGN_UP" && props.type === "PARENT" && (
            <p className="text-xl pb-4 pl-3 text-center">
              <b>Please input parent details</b>
            </p>
          )}
          <Input
            onChange={(newVal) => setEmail(newVal)}
            type="email"
            icon="mail"
            placeholder={`${placeholderName[props.type]} Email Address`}
          />
          <Input
            onChange={(newVal) => setPassword(newVal)}
            type="password"
            placeholder={`${placeholderName[props.type]} Password`}
          />
          {mode === "SIGN_UP" && (
            <>
              <Input
                onChange={(newVal) => setFirstName(newVal)}
                type="text"
                placeholder={`${placeholderName[props.type]} First Name`}
              />
              <Input
                onChange={(newVal) => setLastName(newVal)}
                type="text"
                placeholder={`${placeholderName[props.type]} Last Name`}
              />
              {props.type === "PARENT" && (
                <Input
                  default={referralCode}
                  onChange={(referralCode) => setReferralCode(referralCode)}
                  type="text"
                  placeholder="Referral Code"
                />
              )}
            </>
          )}

          <ButtonPrimary
            onClick={() => {
              if (mode === "LOGIN") {
                signIn();
              } else {
                signUp();
              }
            }}
          >
            {mode === "LOGIN" ? "Sign In" : "Sign Up"}
          </ButtonPrimary>
        </div>
        <div className="inline-flex flex-col gap-3">
          <div
            className="text-normal text-center cursor-pointer rounded-xl bg-white bg-opacity-90 p-1 px-3"
            onClick={() => {
              toast.promise(handleForgotPassword(), {
                success: "Reset password email sent!",
                error: (e) => e.message,
                loading: "Sending password reset email...",
              });
            }}
          >
            <u>Forgot Password</u>
          </div>
          <div className="text-normal text-center rounded-xl bg-white bg-opacity-90 p-1 px-3">
            <>
              {mode === "LOGIN"
                ? "Don't have an account? "
                : "Already have an account? "}
              <button
                className="underline font-semibold cursor-pointer"
                onClick={toggleMode}
              >
                {mode === "LOGIN" ? "Sign Up" : "Sign In"}
              </button>
            </>
          </div>
        </div>
      </div>
    </>
  );
}
